// 2FA step — renders when LoginFlowReducer routes to `need_2fa` after identify
// reports requires_2fa:true (backend IdentityStatus, auth.py:177).
//
// Local state lives here (code, error, cooldown) — the reducer only owns the
// step transition. On verify success we drop back to the email step so the user
// can retry the flow; the production cutover will replace that with a session
// invalidate + navigate("/post-login") once the backend route lands.
import { useEffect, useRef, useState, type FormEvent } from "react";
import { ShieldCheck } from "lucide-react";
import type { LoginFlowReturn } from "../types";
import {
  EmailDisplay,
  ErrorMessage,
  SubmitButton,
  BackButton,
} from "./LoginFormShared";
import { verify2fa } from "../api";
import { FormField } from "@/components/ui/form-field";

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

interface TwoFactorStepProps {
  flow: LoginFlowReturn;
}

interface VerifyOutcome {
  ok: boolean;
  errorMessage: string;
}

async function runVerify(
  code: string,
  signal: AbortSignal,
): Promise<VerifyOutcome> {
  const res = await verify2fa(code, { signal });
  if (res.ok) return { ok: true, errorMessage: "" };
  if (res.status === 0) return { ok: false, errorMessage: "Erro ao conectar" };
  return { ok: false, errorMessage: res.message || "Código inválido" };
}

function useResendCooldown(): { remaining: number; restart: () => void } {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (remaining <= 0) return;
    const id = window.setTimeout(
      () => setRemaining((v) => Math.max(0, v - 1)),
      1000,
    );
    return () => window.clearTimeout(id);
  }, [remaining]);
  return { remaining, restart: () => setRemaining(RESEND_COOLDOWN_SECONDS) };
}

function TwoFactorHeader(): React.ReactElement {
  return (
    <div className="text-center space-y-3">
      <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 flex items-center justify-center">
        <ShieldCheck
          className="w-8 h-8 text-success"
          data-testid="two-factor-icon"
        />
      </div>
      <h2 className="text-xl font-bold text-foreground">
        Verificação em duas etapas
      </h2>
      <p className="text-sm text-foreground/60">
        Digite o código de 6 dígitos enviado para sua conta.
      </p>
    </div>
  );
}

function TwoFactorCodeInput({
  code,
  onCodeChange,
}: {
  code: string;
  onCodeChange: (v: string) => void;
}): React.ReactElement {
  return (
    <FormField
      label="Código de verificação"
      name="two-factor-code"
      required
      counter={{ current: code.length, max: CODE_LENGTH }}
    >
      {(wired) => (
        <input
          {...wired}
          type="text"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={CODE_LENGTH}
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => onCodeChange(e.target.value.replace(/\D/g, ""))}
          placeholder="Código (6 dígitos)"
          data-testid="two-factor-code-input"
          className="w-full bg-surface border border-border rounded-xl py-3.5 px-4 text-center text-foreground tracking-[0.5em] font-mono text-lg focus:outline-none focus:ring-2 focus:border-success focus:ring-emerald-500/20 transition-all shadow-inner shadow-black/30"
          autoFocus
        />
      )}
    </FormField>
  );
}

function ResendButton({
  remaining,
  onResend,
}: {
  remaining: number;
  onResend: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onResend}
      disabled={remaining > 0}
      data-testid="two-factor-resend"
      className="w-full py-2 text-brand-cyan hover:text-brand-cyan disabled:text-foreground/30 text-sm transition-colors"
    >
      {remaining > 0 ? `Reenviar código em ${remaining}s` : "Reenviar código"}
    </button>
  );
}

export function TwoFactorStep({
  flow,
}: TwoFactorStepProps): React.ReactElement {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const verifyAbortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const cooldown = useResendCooldown();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      verifyAbortRef.current?.abort();
    };
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (code.length !== CODE_LENGTH) {
      setError(`Digite o código de ${CODE_LENGTH} dígitos`);
      return;
    }
    setError("");
    setSubmitting(true);
    verifyAbortRef.current?.abort();
    const controller = new AbortController();
    verifyAbortRef.current = controller;
    const outcome = await runVerify(code, controller.signal);
    if (!mountedRef.current || controller.signal.aborted) return;
    setSubmitting(false);
    if (outcome.ok) {
      // TODO(auth-2fa-backend): backend route not yet implemented; keep the
      // user on the form so they see "" rather than an unexpected nav.
      setCode("");
      return;
    }
    setError(outcome.errorMessage);
  }

  function onResend(): void {
    if (cooldown.remaining > 0) return;
    // TODO(auth-2fa-backend): wire to POST /api/v2/auth/resend-2fa once it lands.
    cooldown.restart();
    setError("");
  }

  return (
    <form
      onSubmit={(e) => void onSubmit(e)}
      className="space-y-5 relative z-10"
    >
      <TwoFactorHeader />
      <EmailDisplay email={flow.email} />
      <TwoFactorCodeInput code={code} onCodeChange={setCode} />
      {error && <ErrorMessage message={error} />}
      <SubmitButton
        isLoading={submitting}
        loadingText="VERIFICANDO..."
        submitText="VERIFICAR"
        isDisabled={code.length !== CODE_LENGTH}
      />
      <ResendButton remaining={cooldown.remaining} onResend={onResend} />
      <BackButton onClick={flow.handleBack} isDisabled={submitting} />
    </form>
  );
}
